import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Spinner } from '@/components/common/Spinner';
import { Tab, TabPanel, Tabs, TabsList } from '@/components/common/Tab';
import ExportModal from '@/components/dashboard_components/ExportModal';
import HistoricalApplicationDataGrid from '@/components/dashboard_components/manage_application_dashboard/HistoricalApplicationDataGrid';
import PendingReviewApplicationDataGrid from '@/components/dashboard_components/manage_application_dashboard/PendingReviewApplicationDataGrid';
import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useDashboardGridStatePersistence from '@/hooks/useDashboardGridStatePersistence';
import useApplication from '@/hooks/useApplication';
import { ApplicationDataGridRowProps } from '@/models/Application';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { UserType, userTypeAsString } from '@/models/User';
import { ApplicationStatus, USCountryId } from '@/utils/constants';
import { CustomExportButton } from '@/utils/dataGridUtil';
import { getApplicationHref } from '@/utils/applicationUtil';
import { ApplicationTypes } from '@/models/ApplicationType';
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { programIdToProgramShortName, ProgramNames } from '@/models/Program';
import StatusColumnCellConfig from '@/components/common/StatusColumnCell';

export default function ADFDashboard() {
  const theme = useTheme();
  const { userType } = useContext(UsersContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const { getApplicationsPendingReview, getHistoricApplications } = useApplication();

  const [exportModal, setExportModal] = useState<boolean>(false);
  const [historicAppsLoading, setHistoricAppsLoading] = useState<boolean>(true);
  const [readyForReviewAppsLoading, setReadyForReviewAppsLoading] = useState<boolean>(true);
  const hasFetchedReviewRef = useRef(false);
  const lastFetchedHistoricYearRef = useRef<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(() => {
    const activeTabFromUrl = searchParams.get('tab');
    if (activeTabFromUrl != null) {
      return Number(activeTabFromUrl);
    }

    const activeTabFromSession = sessionStorage.getItem('adfDashboard.activeTab');
    return activeTabFromSession != null ? Number(activeTabFromSession) : 0;
  });
  const { translatedText } = useContext(LocaleContext);
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { localeLanguageCode } = useContext(LocaleContext);
  const { allStatuses } = useContext(ConfigContext);
  const { setAlert } = useContext(SnackbarContext);

  const {
    reviewApps,
    reviewApplicationSortModel,
    reviewApplicationFilterModel,
    historicApps,
    historicApplicationSortModel,
    historicApplicationFilterModel,
    historicApplicationPaginationModel,
    historicApplicationYearSelected,
    reviewApplicationPaginationModel,
    updateReviewApps,
    updateHistoricApps,
    updateReviewApplicationSortModel,
    updateReviewApplicationFilterModel,
    updateReviewApplicationPaginationModel,
    updateHistoricApplicationSortModel,
    updateHistoricApplicationFilterModel,
    updateHistoricApplicationPaginationModel,
    updateHistoricApplicationYearSelected,
  } = useContext(DashboardContext);

  const {
    isHydrated: isReviewGridHydrated,
    handleFilterModelChange: handleReviewFilterModelChange,
    handleSortModelChange: handleReviewSortModelChange,
    handlePaginationModelChange: handleReviewPaginationModelChange,
  } = useDashboardGridStatePersistence({
    filterModel: reviewApplicationFilterModel,
    sortModel: reviewApplicationSortModel,
    paginationModel: reviewApplicationPaginationModel,
    updateFilterModel: updateReviewApplicationFilterModel,
    updateSortModel: updateReviewApplicationSortModel,
    updatePaginationModel: updateReviewApplicationPaginationModel,
    urlKeys: {
      filter: 'reviewFilter',
      sort: 'reviewSort',
      page: 'reviewPage',
      pageSize: 'reviewPageSize',
    },
    sessionKeys: {
      filter: 'adfDashboard.review.filter',
      sort: 'adfDashboard.review.sort',
      page: 'adfDashboard.review.page',
      pageSize: 'adfDashboard.review.pageSize',
    },
  });

  const {
    isHydrated: isHistoricGridHydrated,
    handleFilterModelChange: handleHistoricFilterModelChange,
    handleSortModelChange: handleHistoricSortModelChange,
    handlePaginationModelChange: handleHistoricPaginationModelChange,
  } = useDashboardGridStatePersistence({
    filterModel: historicApplicationFilterModel,
    sortModel: historicApplicationSortModel,
    paginationModel: historicApplicationPaginationModel,
    updateFilterModel: updateHistoricApplicationFilterModel,
    updateSortModel: updateHistoricApplicationSortModel,
    updatePaginationModel: updateHistoricApplicationPaginationModel,
    urlKeys: {
      filter: 'allAppFilter',
      sort: 'allAppSort',
      page: 'allAppPage',
      pageSize: 'allAppPageSize',
    },
    sessionKeys: {
      filter: 'adfDashboard.historic.filter',
      sort: 'adfDashboard.historic.sort',
      page: 'adfDashboard.historic.page',
      pageSize: 'adfDashboard.historic.pageSize',
    },
  });

  const isHydrated = isReviewGridHydrated && isHistoricGridHydrated;

  useEffect(() => {
    const yearFromUrl = searchParams.get('year');
    const yearFromSession = sessionStorage.getItem('adfDashboard.historic.year');

    if (yearFromUrl != null) {
      updateHistoricApplicationYearSelected(yearFromUrl == 'all' ? '' : yearFromUrl);
    } else if (yearFromSession != null) {
      updateHistoricApplicationYearSelected(yearFromSession == 'all' ? '' : yearFromSession);
    }

    if (searchParams.get('tab') == null) {
      const tabFromSession = sessionStorage.getItem('adfDashboard.activeTab');
      if (tabFromSession != null) {
        setActiveTab(Number(tabFromSession));
      }
    }

    // one-time URL/session hydration for tab/year
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sessionStorage.setItem('adfDashboard.activeTab', activeTab.toString());

    if (historicApplicationYearSelected != null) {
      sessionStorage.setItem(
        'adfDashboard.historic.year',
        historicApplicationYearSelected == '' ? 'all' : historicApplicationYearSelected
      );
    }
  }, [activeTab, historicApplicationYearSelected]);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    // Merge against latest URL so this effect does not clobber grid params.
    const latestSearchParams = new URLSearchParams(window.location.search);
    const nextSearchParams = new URLSearchParams(latestSearchParams);
    let hasChanges = false;

    const nextTabInUrl = activeTab.toString();
    const currentTabInUrl = latestSearchParams.get('tab');
    if (currentTabInUrl !== nextTabInUrl) {
      nextSearchParams.set('tab', nextTabInUrl);
      hasChanges = true;
    }

    const nextYearInUrl =
      historicApplicationYearSelected != null
        ? historicApplicationYearSelected == ''
          ? 'all'
          : historicApplicationYearSelected
        : null;
    const currentYearInUrl = latestSearchParams.get('year');

    if (nextYearInUrl == null) {
      if (currentYearInUrl != null) {
        nextSearchParams.delete('year');
        hasChanges = true;
      }
    } else if (currentYearInUrl !== nextYearInUrl) {
      nextSearchParams.set('year', nextYearInUrl);
      hasChanges = true;
    }

    if (hasChanges) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [activeTab, historicApplicationYearSelected, isHydrated, setSearchParams]);

  const dashboardTabs: { [key: number]: string } = {
    0: 'Ready for Review',
    1: 'All Applications',
  };

  const GridToolBarCustom = useCallback(
    (props: GridToolbarContainerProps) => {
      return (
        <GridToolbarContainer {...props}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
          <CustomExportButton onClick={() => setExportModal(true)} />
          <div style={{ flex: 1 }}></div>
          <GridToolbarQuickFilter debounceMs={1500} />
        </GridToolbarContainer>
      );
    },
    [setExportModal]
  );

  const fetchData = useCallback(
    async (
      tab: number | null,
      year: string | null,
      abortController: AbortController,
      options?: { force?: boolean }
    ) => {
      const force = options?.force === true;

      if (tab === 1 && year != null) {
        if (!force && lastFetchedHistoricYearRef.current === year) {
          return;
        }

        setHistoricAppsLoading(true);
        try {
          const applications = await getHistoricApplications(year, abortController);
          if (applications != null) {
            updateHistoricApps(applications);
            lastFetchedHistoricYearRef.current = year;
          } else if (!abortController.signal.aborted) {
            setAlert({
              severity: 'error',
              message: translatedText.dashboard.messageFetchApplicationsError,
            });
          }
        } catch {
          if (!abortController.signal.aborted) {
            setAlert({
              severity: 'error',
              message: translatedText.dashboard.messageFetchApplicationsError,
            });
          }
        } finally {
          setHistoricAppsLoading(false);
        }
      } else if (tab === 0) {
        if (!force && (hasFetchedReviewRef.current || reviewApps.length > 0)) {
          return;
        }

        setReadyForReviewAppsLoading(true);
        try {
          const applications = await getApplicationsPendingReview(abortController);
          updateReviewApps(applications ?? []);
          hasFetchedReviewRef.current = true;
        } catch {
          if (!abortController.signal.aborted) {
            setAlert({
              severity: 'error',
              message: translatedText.dashboard.messageFetchApplicationsError,
            });
          }
        } finally {
          setReadyForReviewAppsLoading(false);
        }
      }
    },
    [
      getApplicationsPendingReview,
      getHistoricApplications,
      reviewApps.length,
      setAlert,
      translatedText.dashboard.messageFetchApplicationsError,
      updateHistoricApps,
      updateReviewApps,
    ]
  );

  const handleRefreshApplications = useCallback(() => {
    const controller = new AbortController();
    void fetchData(
      activeTab,
      activeTab === 1 ? historicApplicationYearSelected : null,
      controller,
      {
        force: true,
      }
    );
  }, [activeTab, fetchData, historicApplicationYearSelected]);

  const handleClearFilters = useCallback(() => {
    const clearedFilterModel: GridFilterModel = {
      items: [],
      quickFilterValues: [],
    };
    const clearedSortModel: GridSortModel = [];

    const nextSearchParams = new URLSearchParams(searchParams);

    if (activeTab === 0) {
      updateReviewApplicationFilterModel(clearedFilterModel);
      updateReviewApplicationSortModel(clearedSortModel);
      sessionStorage.removeItem('adfDashboard.review.filter');
      sessionStorage.removeItem('adfDashboard.review.sort');
      nextSearchParams.delete('reviewFilter');
      nextSearchParams.delete('reviewSort');
    } else if (activeTab === 1) {
      updateHistoricApplicationFilterModel(clearedFilterModel);
      updateHistoricApplicationSortModel(clearedSortModel);
      sessionStorage.removeItem('adfDashboard.historic.filter');
      sessionStorage.removeItem('adfDashboard.historic.sort');
      nextSearchParams.delete('allAppFilter');
      nextSearchParams.delete('allAppSort');
    }

    setSearchParams(nextSearchParams);
  }, [
    activeTab,
    searchParams,
    setSearchParams,
    updateHistoricApplicationFilterModel,
    updateHistoricApplicationSortModel,
    updateReviewApplicationFilterModel,
    updateReviewApplicationSortModel,
  ]);

  useEffect(() => {
    if (activeTab === undefined || activeTab !== 0) return;

    const controller = new AbortController();

    void fetchData(activeTab, null, controller);

    return () => controller.abort('Parameters updated. Data fetching aborted.');
  }, [activeTab, fetchData]);

  useEffect(() => {
    if (activeTab === undefined || activeTab !== 1) return;
    const controller = new AbortController();

    void fetchData(activeTab, historicApplicationYearSelected, controller);

    return () => controller.abort('Parameters updated. Data fetching aborted.');
  }, [activeTab, fetchData, historicApplicationYearSelected]);

  useEffect(() => {
    if (!isHydrated) return;

    // this will only run on the first load since historicApplicationYearSelected is set to null by default
    if (
      applicationCycles != null &&
      applicationCycles.length > 0 &&
      historicApplicationYearSelected == null
    ) {
      //get currently active application cycle year
      const todaysDate = new Date();
      let activeCycle: ApplicationCycle | undefined = applicationCycles.find(
        (cycle: ApplicationCycle) => {
          return todaysDate >= new Date(cycle.openDate) && todaysDate <= new Date(cycle.closeDate);
        }
      );

      // get the most recent application cycle if there is no active cycle
      activeCycle =
        activeCycle != null
          ? activeCycle
          : applicationCycles
              .filter((appCycle: ApplicationCycle) => new Date(appCycle.closeDate) < new Date())
              .sort(
                (a: ApplicationCycle, b: ApplicationCycle) =>
                  new Date(b.closeDate).getFullYear() - new Date(a.closeDate).getFullYear()
              )[0];

      if (activeCycle != null) {
        updateHistoricApplicationYearSelected(activeCycle.year?.toString() ?? '');
      }
    }
  }, [
    applicationCycles,
    isHydrated,
    historicApplicationYearSelected,
    updateHistoricApplicationYearSelected,
  ]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'View Application',
        headerName: '',
        minWidth: 170,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const dashRow: ApplicationDataGridRowProps = params.row as ApplicationDataGridRowProps;
          const appExists = dashRow.applicationId != null && dashRow.applicationTypeId != null;

          return (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Link
                title={translatedText.dashboard.viewApplication}
                role='button'
                data-size='compact'
                href={
                  appExists
                    ? getApplicationHref(
                        dashRow.applicationId,
                        dashRow.applicationTypeId,
                        dashRow.programId
                      )
                    : undefined
                }
                aria-disabled={!appExists}
                tabIndex={!appExists ? -1 : 0}
              >
                {translatedText.dashboard.viewApplication}
              </Link>
            </Box>
          );
        },
      },
      {
        field: 'applicationId',
        headerName: 'Id',
        minWidth: 65, // Adjusted from flex
        flex: 0.5,
      },
      {
        field: 'partner',
        headerName: 'Applicant (Id)',
        headerAlign: 'left',
        align: 'left',
        minWidth: 200, // Adjusted from flex
        flex: 2,
        valueGetter: (value, row) => {
          const dashRow: ApplicationDataGridRowProps = row as ApplicationDataGridRowProps;
          if (dashRow.partner != null && dashRow.partnerId != null) {
            return `${dashRow.partner} (${dashRow.partnerId})`;
          } else {
            return value;
          }
        },
      },
      {
        field: 'applicationCycle',
        headerName: 'Cycle',
        minWidth: 120, // Adjusted from flex
        flex: 1,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'applicationType',
        headerName: 'Program',
        headerAlign: 'left',
        align: 'left',
        minWidth: 180, // Adjusted from flex
        flex: 1,
        valueGetter: (value, row) => {
          const dashRow: ApplicationDataGridRowProps = row as ApplicationDataGridRowProps;

          let applicationTypeName = dashRow.applicationType;

          if (
            userType === UserType.Adf &&
            dashRow.applicationTypeId != null &&
            dashRow.applicationTypeId === ApplicationTypes.GrowthAward
          ) {
            const shortName = programIdToProgramShortName[dashRow.programId];
            if (shortName != null) {
              applicationTypeName = `${applicationTypeName} - ${shortName}`;
            }
          }

          return applicationTypeName ?? value;
        },
      },
      {
        field: 'applicationCompletionRate',
        headerAlign: 'left',
        align: 'left',
        headerName: '%',
        description: 'Percentage of application completed',
        minWidth: 100, // Adjusted from flex
        flex: 0.5,
        valueGetter: (value: number | null, row: ApplicationDataGridRowProps) => {
          // simplify TCUSA GA Calculation
          if (
            row.applicationTypeId === ApplicationTypes.GrowthAward &&
            row.programId == ProgramNames.TreeCityUSA
          ) {
            return row.status === ApplicationStatus.InProgress ||
              row.status === ApplicationStatus.InRevision
              ? '0%'
              : '100%';
          }

          if (value == null) {
            return '0%';
          } else {
            return (value * 100).toFixed(0).toString() + '%';
          }
        },
        sortComparator: (v1: string, v2: string) => {
          const a = Number(v1.replace('%', ''));
          const b = Number(v2.replace('%', ''));

          if (Number.isNaN(a) && Number.isNaN(b)) return 0;
          if (Number.isNaN(a)) return -1;
          if (Number.isNaN(b)) return 1;

          return a - b;
        },
      },
      {
        field: 'submissionDate',
        headerAlign: 'left',
        align: 'left',
        headerName: 'Submission Date',
        minWidth: 150, // Adjusted from flex
        flex: 1,
        valueGetter: (value: string | null) => {
          return value ?? '';
        },
        valueFormatter: (value: string) => {
          if (value == null || value === '') {
            return '';
          }

          return dayjs(value.split('T')[0]).format('LL');
        },
        sortComparator: (v1: string, v2: string) => {
          const a = v1 === '' ? Number.NEGATIVE_INFINITY : dayjs(v1).valueOf();
          const b = v2 === '' ? Number.NEGATIVE_INFINITY : dayjs(v2).valueOf();

          if (Number.isNaN(a) && Number.isNaN(b)) return 0;
          if (Number.isNaN(a)) return -1;
          if (Number.isNaN(b)) return 1;

          return a - b;
        },
      },
      {
        field: 'fullName',
        headerName: 'Submitted By',
        headerAlign: 'left',
        align: 'left',
        minWidth: 150, // Adjusted from flex
        flex: 1,
      },
      {
        field: 'yearsRecognized',
        headerName: 'Years Awarded',
        headerAlign: 'center',
        align: 'center',
        minWidth: 140, // Adjusted from flex
        flex: 1,
      },
      StatusColumnCellConfig(allStatuses, localeLanguageCode),
      {
        field: 'city',
        headerName: 'City',
        minWidth: 150, // Adjusted from flex to allow for long address data
        flex: 1,
        valueGetter: (_, row: ApplicationDataGridRowProps) => {
          return `${row.city ?? ''}`;
        },
        renderHeader: () => {
          return (
            <Tooltip title='Location comes from the primary contact’s address. To update, impersonate the user and make changes in their account settings.'>
              <Box className='grid-header-with-info'>
                City
                <InfoOutlinedIcon fontSize={'small'} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'state',
        headerName: 'State',
        minWidth: 100, // Adjusted from flex to allow for long address data
        flex: 1,
        valueGetter: (_, row: ApplicationDataGridRowProps) => {
          return row.countyId == null || row.countryId === USCountryId
            ? `${row.stateName ?? ''}`
            : 'International';
        },
        renderHeader: () => {
          return (
            <Tooltip title='Location comes from the primary contact’s address. To update, impersonate the user and make changes in their account settings.'>
              <Box className='grid-header-with-info'>
                State <InfoOutlinedIcon fontSize={'small'} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'country',
        headerName: 'Country',
        minWidth: 130, // Adjusted from flex to allow for long address data
        flex: 1,
        valueGetter: (_, row: ApplicationDataGridRowProps) => {
          return `${row.countryName ?? ''}`;
        },
        renderHeader: () => {
          return (
            <Tooltip title='Location comes from the primary contact’s address. To update, impersonate the user and make changes in their account settings.'>
              <Box className='grid-header-with-info'>
                Country <InfoOutlinedIcon fontSize={'small'} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'county',
        headerName: 'County',
        minWidth: 150, // Adjusted from flex to allow for long address data
        flex: 1,
        valueGetter: (_, row: ApplicationDataGridRowProps) => {
          return row.countyId == null || row.countryId === USCountryId
            ? `${row.countyName ?? ''}`
            : 'International';
        },
        renderHeader: () => {
          return (
            <Tooltip title='Location comes from the primary contact’s address. To update, impersonate the user and make changes in their account settings.'>
              <Box className='grid-header-with-info'>
                County <InfoOutlinedIcon fontSize={'small'} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'failedApplicationTransactions',
        headerName: 'Business Central Error',
        hideable: false,
        valueGetter: (_, row: ApplicationDataGridRowProps) => {
          return (
            row.failedApplicationTransactions != null &&
            row.failedApplicationTransactions.length > 0
          );
        },
      },
    ],
    [allStatuses, localeLanguageCode, translatedText.dashboard.viewApplication, userType]
  );

  const handleNoRowsOverlay = useCallback(
    (loading: boolean) => {
      return (
        <Stack height='100%' alignItems='center' justifyContent='center'>
          {loading ? (
            <div>
              <div
                style={{
                  width: 'auto',
                  display: 'inline-block',
                }}
              >
                {'Loading...'}
              </div>
              <div
                style={{
                  display: 'inline-block',
                  position: 'relative',
                  top: '3px',
                  width: '20px',
                  height: '20px',
                  marginLeft: '10px',
                }}
              >
                <CircularProgress size='16px' />
              </div>
            </div>
          ) : (
            <div>{translatedText.dashboard.messageNoApplications}</div>
          )}
        </Stack>
      );
    },
    [translatedText.dashboard.messageNoApplications]
  );

  const handleNoResultsOverlay = useCallback(() => {
    return (
      <Stack height='100%' alignItems='center' justifyContent='center'>
        No Applications Found. Please adjust your filters and try again.
      </Stack>
    );
  }, []);
  const datagridSx = {
    marginLeft: '10px',
    marginRight: '10px',
    flex: '1 1 auto',
    '.grid-header-with-info': {
      display: 'flex',
      alignItems: 'space-between',
      justifyItems: 'center',
      gap: 1,
    },
    '& .bc-error': {
      color: theme.palette.error.main,
      fontWeight: 'bold',
      // onhover keep the text color red
      '&:hover': {
        color: theme.palette.error.main,
      },
    },
  };

  const tabPanelSx = {
    flexDirection: 'column',
    width: '100%',
    height: '65vh',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        margin: '10px',
        overflow: 'hidden',
      }}
    >
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{
          minHeight: '110px',
          position: 'relative',
        }}
      >
        <Box flex={1} textAlign='center'>
          <Typography variant='h1'>
            {`${userType != null ? userTypeAsString(userType) : ''} Dashboard`.trim()}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, v) => {
          const tabValue = v as number;
          setActiveTab(tabValue);
        }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <TabsList
            style={{
              display: 'flex',
              marginBottom: '1rem',
            }}
          >
            <Tab label='Item One' value={0}>
              {dashboardTabs[0].toLocaleUpperCase()}
            </Tab>
            <Tab label='Item Two' value={1}>
              {dashboardTabs[1].toLocaleUpperCase()}
            </Tab>
          </TabsList>
          <Stack direction='row' spacing={1}>
            <Button
              variant='secondary'
              size='small'
              onClick={handleClearFilters}
              sx={{
                fontSize: '12px',
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant='secondary'
              size='small'
              onClick={handleRefreshApplications}
              sx={{
                fontSize: '12px',
              }}
            >
              Refresh Applications
            </Button>
          </Stack>
        </Box>

        <TabPanel value={0} style={tabPanelSx}>
          {reviewApps != null ? (
            <PendingReviewApplicationDataGrid
              columns={columns}
              dataGridSx={datagridSx}
              customExportToolbar={GridToolBarCustom}
              onNoRowsOverlay={handleNoRowsOverlay}
              onNoResultsOverlay={handleNoResultsOverlay}
              loading={readyForReviewAppsLoading}
              onFilterModelChange={handleReviewFilterModelChange}
              onSortModelChange={handleReviewSortModelChange}
              onPaginationModelChange={handleReviewPaginationModelChange}
            />
          ) : (
            <Spinner />
          )}
        </TabPanel>
        <TabPanel value={1} style={tabPanelSx}>
          {historicApps != null ? (
            <HistoricalApplicationDataGrid
              columns={columns}
              dataGridSx={datagridSx}
              customExportTool={GridToolBarCustom}
              onNoRowsOverlay={handleNoRowsOverlay}
              onNoResultsOverlay={handleNoResultsOverlay}
              loading={historicAppsLoading}
              onFilterModelChange={handleHistoricFilterModelChange}
              onSortModelChange={handleHistoricSortModelChange}
              onPaginationModelChange={handleHistoricPaginationModelChange}
            />
          ) : (
            <Spinner />
          )}
        </TabPanel>
      </Tabs>

      <ExportModal open={exportModal} onClose={() => setExportModal(false)} />
    </Box>
  );
}
